import i18n from "i18next";

const DEFAULT_MESSAGES = {
  en: "An error occurred while completing the operation.",
  it: "Si è verificato un errore durante l'operazione.",
  sq: "Ndodhi një gabim gjatë veprimit.",
};

const EXACT_TRANSLATIONS = {
  "Validation error": "I dati inseriti non sono validi.",
  Unauthorized: "Sessione non valida o scaduta. Effettua nuovamente l'accesso.",
  Forbidden: "Non hai i permessi necessari per eseguire questa operazione.",
  "Forbidden: insufficient permissions": "Non hai i permessi necessari per eseguire questa operazione.",
  "Internal server error": "Si è verificato un errore interno del server.",
  "Token not found": "Sessione non valida o scaduta. Effettua nuovamente l'accesso.",
  "Invalid credentials": "Credenziali non valide.",
  "Invalid or expired reset token": "Il collegamento di ripristino non è valido oppure è scaduto.",
  "User not found": "Utente non trovato.",
  "Invalid user id": "Utente non valido.",
  "Unable to retrieve permissions": "Impossibile recuperare i permessi dell'utente.",
  "Package not found": "Pacchetto non trovato.",
  "Product not found": "Articolo non trovato.",
  "Room not found": "Sala non trovata.",
  "Reservation not found": "Prenotazione non trovata.",
  "Tenant not found": "Struttura non trovata.",
};

const PHRASE_TRANSLATIONS = [
  [/\bPackage not found\b/gi, "Pacchetto non trovato"],
  [/\bProduct not found\b/gi, "Articolo non trovato"],
  [/\bUser not found\b/gi, "Utente non trovato"],
  [/\bRoom not found\b/gi, "Sala non trovata"],
  [/\bReservation not found\b/gi, "Prenotazione non trovata"],
  [/\bClient not found\b/gi, "Cliente non trovato"],
  [/\bInvoice not found\b/gi, "Fattura non trovata"],
  [/\bInvalid credentials\b/gi, "Credenziali non valide"],
  [/\binsufficient permissions\b/gi, "permessi insufficienti"],
];

const ENGLISH_ERROR_WORDS = /\b(required|invalid|not found|already|must|cannot|could not|failed|forbidden|unauthorized|not authorized|unable|missing|error|not allowed|not available|denied|bad request|conflict|something went wrong|timed? out|too many requests|unknown)\b/i;
const ENGLISH_SENTENCE_WORDS = /\b(the|this|that|is|are|was|were|has|have|with|without|for|from|please|try|operation|request|response|order|payment|invoice|reservation|room|table|user|client|product|package)\b/i;
const TECHNICAL_ERROR = /\b(sqlite|prisma|sequelize|constraint|econn|enotfound|syntaxerror|typeerror|referenceerror|stack trace|database)\b|\bat\s+\w+.*\.(js|ts):\d+/i;

function extractMessage(error) {
  return String(
    error?.response?.data?.error
    || error?.response?.data?.message
    || error?.error
    || error?.message
    || error
    || ""
  ).trim();
}

function languageCode(language) {
  return String(language || "it").toLowerCase().split(/[-_]/)[0];
}

function defaultMessage(language) {
  return DEFAULT_MESSAGES[languageCode(language)] || DEFAULT_MESSAGES.en;
}

function italianHttpMessage(status) {
  const messages = {
    400: "La richiesta non è valida. Controlla i dati inseriti.",
    401: "Sessione non valida o scaduta. Effettua nuovamente l'accesso.",
    403: "Non hai i permessi necessari per eseguire questa operazione.",
    404: "La risorsa richiesta non è stata trovata.",
    409: "L'operazione non può essere completata perché i dati sono in conflitto.",
    422: "I dati inseriti non sono validi.",
    500: "Si è verificato un errore interno del server.",
    502: "Il servizio non è momentaneamente disponibile.",
    503: "Il servizio non è momentaneamente disponibile.",
  };
  return messages[Number(status)] || `Errore di comunicazione con il server (codice ${status}).`;
}

export function italianErrorMessage(error, fallback = DEFAULT_MESSAGES.it) {
  const message = extractMessage(error);
  if (!message) return fallback;
  if (EXACT_TRANSLATIONS[message]) return EXACT_TRANSLATIONS[message];

  const httpStatus = message.match(/^(?:HTTP error! status:|Request failed with status code)\s*(\d+)/i);
  if (httpStatus) return italianHttpMessage(httpStatus[1]);

  const errorStatus = Number(error?.status || error?.response?.status);
  if ([401, 403].includes(errorStatus) || errorStatus >= 500) {
    return italianHttpMessage(errorStatus);
  }
  if (errorStatus && (/^HTTP\b/i.test(message) || message === String(errorStatus))) {
    return italianHttpMessage(errorStatus);
  }

  if (/failed to fetch|network\s*error|networkerror|load failed|fetch failed/i.test(message)) {
    return "Impossibile contattare il server. Controlla la connessione e riprova.";
  }

  if (/unexpected token|not valid json|json parse/i.test(message)) {
    return "Il server ha restituito una risposta non valida. Riprova o contatta l'assistenza.";
  }

  if (TECHNICAL_ERROR.test(message)) return fallback;

  let translated = message;
  PHRASE_TRANSLATIONS.forEach(([pattern, replacement]) => {
    translated = translated.replace(pattern, replacement);
  });

  if (translated !== message && !ENGLISH_ERROR_WORDS.test(translated)) return translated;
  if (ENGLISH_ERROR_WORDS.test(message) || ENGLISH_SENTENCE_WORDS.test(message)) return fallback;

  // Business messages already supplied in Italian remain specific and useful.
  return message;
}

export function localizedErrorMessage(error, fallback) {
  const language = languageCode(i18n.resolvedLanguage || i18n.language || "it");
  const localizedFallback = fallback || defaultMessage(language);

  if (language === "it") return italianErrorMessage(error, localizedFallback);

  const message = extractMessage(error);
  if (!message) return localizedFallback;
  if (TECHNICAL_ERROR.test(message)) return localizedFallback;

  const httpStatus = message.match(/^(?:HTTP error! status:|Request failed with status code)\s*(\d+)/i);
  if (httpStatus) return localizedFallback;

  return message;
}

export const DEFAULT_ERROR_MESSAGE = DEFAULT_MESSAGES.it;
